import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { getVaultStatsSeries } from "../VaultQueries";
import SegmentedTabs from "../../../../Components/Selection/SegmentedTabs";

/**
 * Vault performance chart — share price / deposits / volume / withdrawals over time.
 *
 * Themed to match the app (light surface, purple #5b1dee accent, General Sans),
 * mirrors the SAI vault chart's UX: a metric dropdown + 7D/30D/ALL range toggle,
 * with a filled area series. Data comes from the keeper's lp.vaultStats query.
 */

/* The chart follows the page's accent rather than a literal purple, so it
   lifts in dark mode with everything else. Resolved at render because SVG
   `stroke` and the gradient stops cannot take a CSS variable through Recharts'
   props — see `readAccent` below. */
const ACCENT_FALLBACK = "#5b1dee";

const readAccent = () => {
  if (typeof window === "undefined") return ACCENT_FALLBACK;
  const v = getComputedStyle(document.documentElement).getPropertyValue("--cat-accent").trim();
  return v || ACCENT_FALLBACK;
};

const METRICS = [
  { key: "sharePrice", label: "Share Price", kind: "price" },
  { key: "depositsUsd", label: "Deposits", kind: "usd" },
  { key: "volumeUsd", label: "Volume", kind: "usd" },
  { key: "withdrawalsUsd", label: "Withdrawals", kind: "usd" },
];

const RANGES = ["7D", "30D", "ALL"];

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
};
// Share price moves very slowly (fees only), so day-to-day changes live in the
// 5th–6th decimal. Use 6dp in the tooltip so a hover actually shows the difference.
const fmtPrice = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const fmtPricePrecise = (n) => `$${(Number(n) || 0).toFixed(6)}`;

const fmtAxisDate = (ts) =>
  new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const VaultPerformanceChart = ({ vaultAddress, refreshKey = 0 }) => {
  const [range, setRange] = useState("7D");
  const [metric, setMetric] = useState(METRICS[0]); // Share Price default
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accent, setAccent] = useState(ACCENT_FALLBACK);
  const menuRef = useRef(null);

  // Re-read on mount and whenever the theme or the open market changes — both
  // are published as attributes on <html>, which is what the accent derives
  // from (neumorph2026.css §0).
  useEffect(() => {
    const sync = () => setAccent(readAccent());
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-cat"] });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getVaultStatsSeries(range, vaultAddress)
      .then((data) => {
        if (alive) setRows(data);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range, vaultAddress, refreshKey]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fmtValue = metric.kind === "price" ? fmtPrice : fmtUsd;

  const chartData = useMemo(
    () => rows.map((r) => ({ ts: r.ts, value: r[metric.key] })),
    [rows, metric]
  );

  // Totals shown above the chart (TVL-style header): latest share price, summed flows.
  const headline = useMemo(() => {
    if (!rows.length) return null;
    const last = rows[rows.length - 1];
    if (metric.kind === "price") return fmtPrice(last.sharePrice);
    const sum = rows.reduce((s, r) => s + (r[metric.key] || 0), 0);
    return fmtUsd(sum);
  }, [rows, metric]);

  return (
    <div className="ce-vault-panel mb-6 w-full overflow-hidden" style={{ padding: 16 }}>
      {/* Header: metric value + controls */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="ce-eyebrow">{metric.label}</div>
          <div className="ce-figure" style={{ fontWeight: 600, fontSize: 22, color: "var(--ink)" }}>
            {loading ? "—" : headline ?? "—"}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Range toggle — a sunken track with one raised thumb, the same
              object as every other switch in the app. It was three buttons
              with an accent fill under whichever was current. */}
          <SegmentedTabs
            label="Chart range"
            size={26}
            value={range}
            onChange={setRange}
            items={RANGES.map((r) => ({ value: r, label: r }))}
          />

          {/* Metric dropdown — `.ce-menu-trigger` / `.ce-menu`, the same
              dropdown the oracle charts on the market pages use. */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="ce-menu-trigger"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {metric.label}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2.5 3.5L5 6L7.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {menuOpen && (
              <div className="ce-menu" role="listbox" style={{ minWidth: 150 }}>
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    role="option"
                    aria-checked={m.key === metric.key}
                    aria-selected={m.key === metric.key}
                    className="ce-menu-item"
                    onClick={() => {
                      setMetric(m);
                      setMenuOpen(false);
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="w-full h-[260px]">
        {loading ? (
          <div
            className="w-full h-full rounded-md animate-[shimmer_1.5s_ease-in-out_infinite]"
            style={{
              background: "linear-gradient(90deg, var(--surface-3) 0%, var(--surface-2) 50%, var(--surface-3) 100%)",
              backgroundSize: "200% 100%",
              boxShadow: "var(--inset-sm)",
            }}
          />
        ) : chartData.length < 2 ? (
          <div className="w-full h-full flex items-center justify-center text-[13px]" style={{ color: "var(--ink-3)" }}>
            Not enough data to display yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="vaultAreaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--line-2)" vertical={false} />
              <XAxis
                dataKey="ts"
                tickFormatter={fmtAxisDate}
                tick={{ fill: "var(--ink-4)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                minTickGap={40}
              />
              <YAxis
                tickFormatter={(v) => (metric.kind === "price" ? Number(v).toFixed(2) : fmtUsd(v))}
                tick={{ fill: "var(--ink-4)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={56}
                domain={["auto", "auto"]}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "var(--r-inner, 14px)",
                  border: 0,
                  background: "var(--surface)",
                  boxShadow: "var(--float)",
                  color: "var(--ink)",
                  fontFamily: "General Sans",
                  fontSize: 12,
                }}
                labelStyle={{ color: "var(--ink-3)" }}
                itemStyle={{ color: "var(--ink)" }}
                labelFormatter={(ts) =>
                  new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                }
                formatter={(v) => [metric.kind === "price" ? fmtPricePrecise(v) : fmtValue(v), metric.label]}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={accent}
                strokeWidth={2}
                fill="url(#vaultAreaFill)"
                dot={false}
                activeDot={{ r: 4, fill: accent }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default VaultPerformanceChart;
